/**
 * Save / persistence — a FIRST-CLASS, versioned, corruption-resistant system.
 *
 * Goals (per SPEC):
 *  - Resume the EXACT mid-run state, bit-identical (deck, piles, HP, Cycles,
 *    Programs, Trace, map position, full RNG state, runner statuses).
 *  - Versioned schema + migration so saves survive updates.
 *  - Integrity check (checksum) so corrupt/partial saves FAIL SAFE, never crash.
 *  - Export/import as a portable string.
 *
 * This module is pure: it serializes/deserializes RunState <-> string. The thin
 * storage layer (localStorage) lives in the renderer and just calls these.
 */

import { CURRENT_SAVE_VERSION, runIdFor } from "./api";
import { canonicalJson } from "./state";
import type { CharacterId, RunState } from "./types";

/** Magic prefix so we can detect "is this even our save format". */
const SAVE_MAGIC = "NULLRUN1";

/** The envelope we actually serialize. */
interface SaveEnvelope {
  magic: string;
  version: number; // schema version of `run`
  checksum: string; // checksum of canonicalJson(run)
  run: RunState;
}

export type LoadResult =
  | { ok: true; state: RunState; migratedFrom?: number }
  | { ok: false; error: LoadError };

export type LoadError =
  | "empty"
  | "bad-format"
  | "not-nullrun"
  | "checksum-mismatch"
  | "unsupported-version"
  | "migration-failed";

// ---------------------------------------------------------------------------
// Checksum (deterministic, fast). FNV-1a over the canonical JSON.
// ---------------------------------------------------------------------------

export function checksum(s: string): string {
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

// ---------------------------------------------------------------------------
// Serialize
// ---------------------------------------------------------------------------

/** Serialize a run into a portable, integrity-checked string. */
export function serializeRun(run: RunState): string {
  const body = canonicalJson(run);
  const envelope: SaveEnvelope = {
    magic: SAVE_MAGIC,
    version: run.version,
    checksum: checksum(body),
    run,
  };
  return JSON.stringify(envelope);
}

// ---------------------------------------------------------------------------
// Deserialize (fail-safe)
// ---------------------------------------------------------------------------

/**
 * Parse + validate + migrate a save string. NEVER throws — always returns a
 * LoadResult. Corrupt/partial/foreign data yields { ok:false } with a reason.
 */
export function deserializeRun(raw: string | null | undefined): LoadResult {
  if (raw == null || raw.length === 0) {
    return { ok: false, error: "empty" };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: "bad-format" };
  }

  if (!isObject(parsed)) return { ok: false, error: "bad-format" };
  const env = parsed as Partial<SaveEnvelope>;

  if (env.magic !== SAVE_MAGIC) return { ok: false, error: "not-nullrun" };
  if (typeof env.version !== "number") {
    return { ok: false, error: "bad-format" };
  }
  if (!isObject(env.run)) return { ok: false, error: "bad-format" };

  // Integrity: recompute checksum on the canonical run body.
  let body: string;
  try {
    body = canonicalJson(env.run);
  } catch {
    return { ok: false, error: "bad-format" };
  }
  if (typeof env.checksum !== "string" || checksum(body) !== env.checksum) {
    return { ok: false, error: "checksum-mismatch" };
  }

  // Migration: bring older schemas up to current.
  if (env.version > CURRENT_SAVE_VERSION) {
    // Save from a NEWER build — we don't know how to read it. Fail safe.
    return { ok: false, error: "unsupported-version" };
  }

  let run = env.run as RunState;
  let migratedFrom: number | undefined;
  if (env.version < CURRENT_SAVE_VERSION) {
    const migrated = migrate(run, env.version);
    if (!migrated.ok) return { ok: false, error: "migration-failed" };
    run = migrated.run;
    migratedFrom = env.version;
  }

  // Final shape validation (cheap structural sanity).
  if (!validateRunShape(run)) return { ok: false, error: "bad-format" };

  return migratedFrom != null
    ? { ok: true, state: run, migratedFrom }
    : { ok: true, state: run };
}

// ---------------------------------------------------------------------------
// Migration
// ---------------------------------------------------------------------------

type MigrationResult = { ok: true; run: RunState } | { ok: false };

/**
 * Stepwise migration ladder. Each step upgrades version N -> N+1, applied in
 * order until the save reaches CURRENT. Each step is a small, pure transform on
 * a loosely-typed bag (we don't trust an old save to match the current types).
 * Always stamps run.version = CURRENT at the end. Returns { ok:false } if any
 * step is undefined (an unknown version we can't safely upgrade).
 */
type AnyRun = Record<string, unknown>;

/** Migration steps keyed by the version they upgrade FROM (N -> N+1). */
const MIGRATIONS: Record<number, (r: AnyRun) => AnyRun> = {
  // v1 -> v2: introduce the deterministic `runId`. v1 saves never had one, so
  // we synthesize it from the run's own (seed, character, heat) — exactly what
  // newRun does today — keeping it reproducible and consistent with new runs.
  1: (r) => {
    const seed = typeof r.seed === "string" ? r.seed : "";
    const character = (r.character as CharacterId) ?? "ghost";
    const heat = typeof r.heat === "number" ? r.heat : 0;
    r.runId = runIdFor(seed, character, heat);
    return r;
  },
};

function migrate(run: RunState, fromVersion: number): MigrationResult {
  try {
    let v = fromVersion;
    let out = run as unknown as AnyRun;
    while (v < CURRENT_SAVE_VERSION) {
      const step = MIGRATIONS[v];
      if (!step) {
        // No migration defined for this step — cannot proceed safely.
        return { ok: false };
      }
      out = step(out);
      v += 1;
    }
    out.version = CURRENT_SAVE_VERSION;
    return { ok: true, run: out as unknown as RunState };
  } catch {
    return { ok: false };
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Cheap structural validation — confirms the critical fields exist with the
 * right primitive types. Not a full schema validator; its job is to catch
 * partial/garbled saves that passed the checksum (shouldn't happen, but defense
 * in depth) before they reach gameplay code.
 */
export function validateRunShape(run: unknown): run is RunState {
  if (!isObject(run)) return false;
  const r = run as Record<string, unknown>;
  if (typeof r.version !== "number") return false;
  if (typeof r.runId !== "string") return false;
  if (typeof r.seed !== "string") return false;
  if (typeof r.character !== "string") return false;
  if (!isObject(r.rng)) return false;
  const rng = r.rng as Record<string, unknown>;
  for (const k of ["a", "b", "c", "d"]) {
    if (typeof rng[k] !== "number") return false;
  }
  if (typeof r.hp !== "number" || typeof r.maxHp !== "number") return false;
  if (!Array.isArray(r.deck)) return false;
  if (!Array.isArray(r.programs)) return false;
  if (!isObject(r.map)) return false;
  if (typeof r.phase !== "string") return false;
  if (typeof r.nextUid !== "number") return false;
  return true;
}

// ---------------------------------------------------------------------------
// Export / import (portable share strings)
// ---------------------------------------------------------------------------

/**
 * Export a save as a portable string. We base64-encode the serialized envelope
 * so it copy/pastes cleanly. Uses a runtime-agnostic base64.
 */
export function exportSave(run: RunState): string {
  const json = serializeRun(run);
  return base64Encode(json);
}

/** Import a save from an exported string. Fail-safe like deserializeRun. */
export function importSave(encoded: string | null | undefined): LoadResult {
  if (encoded == null || encoded.length === 0) {
    return { ok: false, error: "empty" };
  }
  let json: string;
  try {
    json = base64Decode(encoded.trim());
  } catch {
    return { ok: false, error: "bad-format" };
  }
  return deserializeRun(json);
}

// Runtime-agnostic base64 (works in Node + browser without Buffer/atob assumptions).
const B64 =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

export function base64Encode(input: string): string {
  // Encode as UTF-8 bytes first.
  const bytes = utf8Bytes(input);
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i]!;
    const b1 = i + 1 < bytes.length ? bytes[i + 1]! : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2]! : 0;
    const triple = (b0 << 16) | (b1 << 8) | b2;
    out += B64[(triple >> 18) & 0x3f];
    out += B64[(triple >> 12) & 0x3f];
    out += i + 1 < bytes.length ? B64[(triple >> 6) & 0x3f] : "=";
    out += i + 2 < bytes.length ? B64[triple & 0x3f] : "=";
  }
  return out;
}

export function base64Decode(input: string): string {
  const clean = input.replace(/[^A-Za-z0-9+/=]/g, "");
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const ch of clean) {
    if (ch === "=") break;
    const val = B64.indexOf(ch);
    if (val < 0) throw new Error("bad base64");
    buffer = (buffer << 6) | val;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  return utf8Decode(bytes);
}

function utf8Bytes(str: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < str.length; i++) {
    let code = str.charCodeAt(i);
    if (code < 0x80) {
      out.push(code);
    } else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code >= 0xd800 && code <= 0xdbff) {
      // surrogate pair
      const hi = code;
      const lo = str.charCodeAt(++i);
      code = 0x10000 + ((hi - 0xd800) << 10) + (lo - 0xdc00);
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    } else {
      out.push(
        0xe0 | (code >> 12),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return out;
}

function utf8Decode(bytes: number[]): string {
  let out = "";
  let i = 0;
  while (i < bytes.length) {
    const b0 = bytes[i++]!;
    if (b0 < 0x80) {
      out += String.fromCharCode(b0);
    } else if ((b0 & 0xe0) === 0xc0) {
      const b1 = bytes[i++]! & 0x3f;
      out += String.fromCharCode(((b0 & 0x1f) << 6) | b1);
    } else if ((b0 & 0xf0) === 0xe0) {
      const b1 = bytes[i++]! & 0x3f;
      const b2 = bytes[i++]! & 0x3f;
      out += String.fromCharCode(((b0 & 0x0f) << 12) | (b1 << 6) | b2);
    } else {
      const b1 = bytes[i++]! & 0x3f;
      const b2 = bytes[i++]! & 0x3f;
      const b3 = bytes[i++]! & 0x3f;
      const cp =
        ((b0 & 0x07) << 18) | (b1 << 12) | (b2 << 6) | b3;
      const off = cp - 0x10000;
      out += String.fromCharCode(0xd800 + (off >> 10), 0xdc00 + (off & 0x3ff));
    }
  }
  return out;
}
